const averageDistanceBetweenClusters = (leftCluster, rightCluster, getDistance) => {
  let totalDistance = 0;
  let pairCount = 0;

  leftCluster.memberIds.forEach((leftId) => {
    rightCluster.memberIds.forEach((rightId) => {
      totalDistance += getDistance(leftId, rightId);
      pairCount += 1;
    });
  });

  if (pairCount === 0) {
    return Number.POSITIVE_INFINITY;
  }

  return totalDistance / pairCount;
};

const minMemberName = (cluster) => {
  let best = '';
  cluster.members.forEach((member, index) => {
    if (index === 0 || member.name.localeCompare(best) < 0) {
      best = member.name;
    }
  });
  return best;
};

const mergeOrderedMembers = (leftCluster, rightCluster) => {
  if (leftCluster.members.length > rightCluster.members.length) {
    return [...leftCluster.members, ...rightCluster.members];
  }
  if (rightCluster.members.length > leftCluster.members.length) {
    return [...rightCluster.members, ...leftCluster.members];
  }

  return minMemberName(leftCluster).localeCompare(minMemberName(rightCluster)) <= 0
    ? [...leftCluster.members, ...rightCluster.members]
    : [...rightCluster.members, ...leftCluster.members];
};

export const hierarchicalLeafOrder = (samples, getDistance) => {
  if (samples.length <= 1) {
    return [...samples];
  }

  let clusters = samples.map((sample) => ({
    members: [sample],
    memberIds: [sample.id]
  }));

  while (clusters.length > 1) {
    let bestPair = null;
    let bestAverageDistance = Number.POSITIVE_INFINITY;
    let bestPairKey = '';

    for (let leftIndex = 0; leftIndex < clusters.length; leftIndex += 1) {
      for (let rightIndex = leftIndex + 1; rightIndex < clusters.length; rightIndex += 1) {
        const leftCluster = clusters[leftIndex];
        const rightCluster = clusters[rightIndex];
        const averageDistance = averageDistanceBetweenClusters(leftCluster, rightCluster, getDistance);
        const pairKey = `${minMemberName(leftCluster)}:${minMemberName(rightCluster)}`;
        const isBetter = (
          bestPair == null
          || averageDistance < bestAverageDistance
          || (averageDistance === bestAverageDistance && pairKey.localeCompare(bestPairKey) < 0)
        );

        if (isBetter) {
          bestPair = [leftIndex, rightIndex];
          bestAverageDistance = averageDistance;
          bestPairKey = pairKey;
        }
      }
    }

    if (bestPair == null) {
      break;
    }

    const [leftIndex, rightIndex] = bestPair;
    const leftCluster = clusters[leftIndex];
    const rightCluster = clusters[rightIndex];
    const mergedMembers = mergeOrderedMembers(leftCluster, rightCluster);
    const mergedMemberIds = mergedMembers.map((sample) => sample.id);

    clusters = clusters.filter((_, index) => index !== leftIndex && index !== rightIndex);
    clusters.push({
      members: mergedMembers,
      memberIds: mergedMemberIds
    });
  }

  if (clusters.length === 1) {
    return clusters[0].members;
  }

  return clusters
    .sort((leftCluster, rightCluster) => (
      minMemberName(leftCluster).localeCompare(minMemberName(rightCluster))
    ))
    .flatMap((cluster) => cluster.members);
};

export default hierarchicalLeafOrder;
